import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Compass, Users, Globe } from 'lucide-react';
import mEmblem from '../assets/brand/midgard_m_emblem.png';
import SiteFooter, { DiscordGlyph } from '../components/site/SiteFooter';
import { DISCORD_INVITE_URL } from '../config/community';

const features = [
  {
    icon: Compass,
    title: 'Explore Realms',
    desc: 'From Midgard to Asgard and beyond.',
  },
  {
    icon: Users,
    title: 'Create & Belong',
    desc: 'Share your story. Find your people.',
  },
  {
    icon: Globe,
    title: 'A Living Universe',
    desc: 'Realms, stories and people connected to the world.',
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col bg-[#0a050f]" data-testid="landing-page">
      {/* Hero: the artwork fills exactly the first screen on every device */}
      <section className="relative min-h-[100svh] flex flex-col">
      <div className="absolute inset-0 z-0">
        <picture>
          <source srcSet="/midgard-world-bg.webp" type="image/webp" />
          <img
            src="/midgard-world-bg.jpg"
            alt=""
            className="w-full h-full object-cover object-[50%_35%]"
            loading="eager"
          />
        </picture>
        {/* Controlled gradient overlay — not heavy, preserving brightness */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 70% 60% at 50% 42%, rgba(0,0,0,0.45) 0%, transparent 70%),
              linear-gradient(180deg, rgba(8,4,18,0.3) 0%, rgba(8,4,18,0.15) 40%, rgba(8,4,18,0.6) 88%, #0a050f 100%)
            `,
          }}
        />
      </div>

      {/* Top Navigation */}
      <header className="relative z-20 w-full">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-[max(1rem,env(safe-area-inset-top))] pb-3 sm:py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={mEmblem} alt="Midgard" className="w-9 h-9 object-contain" style={{ filter: 'drop-shadow(0 0 10px rgba(139,92,246,0.6))' }} data-testid="landing-nav-emblem" />
            <span className="mg-wordmark text-white text-lg" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>Midgard</span>
          </div>
          <nav className="flex items-center gap-4 sm:gap-8">
            <Link to="/about" className="hidden sm:inline text-white/70 hover:text-white text-sm font-medium">About</Link>
            <Link
              to="/auth?mode=login"
              className="mg-btn mg-btn-ghost text-white/80 hover:text-white text-sm px-4 py-2 border border-white/15 hover:border-white/30 hover:bg-white/5 backdrop-blur-sm"
              data-testid="nav-login-btn"
            >
              Log In
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero Center Content */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-5 sm:px-6 pb-8 sm:pb-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          className="w-full max-w-xl"
        >
          {/* Emblem */}
          <div className="flex justify-center mb-4 sm:mb-6">
            <div className="relative">
              <div className="absolute inset-0 w-32 h-32 -m-4 rounded-full blur-2xl" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.35), transparent 70%)' }} />
              <img
                src={mEmblem}
                alt="Midgard"
                className="relative w-20 h-20 sm:w-28 sm:h-28 object-contain mg-emblem-float"
                style={{ filter: 'drop-shadow(0 0 20px rgba(139,92,246,0.6))' }}
                data-testid="landing-emblem"
              />
            </div>
          </div>

          {/* Wordmark */}
          <h1
            className="mg-wordmark text-white text-4xl sm:text-5xl lg:text-6xl mb-3"
            style={{ textShadow: '0 2px 24px rgba(0,0,0,0.5), 0 0 60px rgba(139,92,246,0.2)' }}
            data-testid="landing-wordmark"
          >
            Midgard
          </h1>

          {/* Subtitle track */}
          <p
            className="mg-subtitle-track text-white/60 mb-6"
            style={{ textShadow: '0 1px 8px rgba(0,0,0,0.6)' }}
          >
            People &bull; Worlds &bull; Stories &bull; Beyond
          </p>

          {/* Primary statement */}
          <h2
            className="mg-display-title text-white text-xl sm:text-2xl lg:text-3xl mb-4"
            style={{ textShadow: '0 2px 16px rgba(0,0,0,0.5)' }}
          >
            Enter a Living Social World
          </h2>

          {/* Supporting copy */}
          <p
            className="text-white/80 text-[15px] sm:text-base max-w-md mx-auto mb-8 sm:mb-10 leading-relaxed"
            style={{ textShadow: '0 1px 8px rgba(0,0,0,0.5)' }}
          >
            Connect, create, and journey from the human realms of Midgard into a living mythic universe.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center w-full max-w-sm sm:max-w-none mx-auto">
            <Link
              to="/auth"
              className="mg-btn mg-btn-primary w-full sm:w-auto h-12 text-base px-8 inline-flex items-center justify-center"
              data-testid="landing-join-btn"
            >
              Create Account
            </Link>
            <Link
              to="/auth?mode=login"
              className="mg-btn mg-btn-secondary w-full sm:w-auto h-12 text-base px-8 inline-flex items-center justify-center text-white/90 bg-black/30 border-white/20 backdrop-blur-md hover:border-[hsl(var(--mg-gold)/0.5)] hover:bg-black/40"
              data-testid="landing-login-btn"
            >
              Log In
            </Link>
          </div>
        </motion.div>
      </main>
      </section>

      {/* Feature Strip */}
      <section className="relative z-10 w-full pb-8 pt-2 px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4"
        >
          {features.map((f, i) => (
            <div
              key={i}
              className="mg-frame relative p-4 sm:p-5 bg-white/[0.04] border-white/10 flex sm:block items-center gap-4 text-left sm:text-center group hover:border-[hsl(var(--mg-purple)/0.4)] transition-all duration-300"
              data-testid={`landing-feature-${i}`}
            >
              <f.icon className="w-7 h-7 sm:w-6 sm:h-6 shrink-0 sm:mx-auto sm:mb-3 text-[hsl(var(--mg-purple))] opacity-90 group-hover:opacity-100 transition-opacity" />
              <div className="min-w-0">
                <h3 className="font-display text-white text-[15px] sm:text-sm font-semibold mb-1 sm:mb-1.5 tracking-wide">{f.title}</h3>
                <p className="text-white/65 text-sm sm:text-xs leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Community */}
      <section className="relative z-10 w-full px-4 sm:px-6 pb-12" data-testid="landing-community">
        <div className="mg-frame max-w-4xl mx-auto p-5 sm:p-8 bg-white/[0.04] border-white/10 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-white text-lg sm:text-xl font-semibold tracking-wide">Join the Midgard Community</h2>
            <p className="mt-2 text-white/70 text-sm leading-relaxed">
              Meet fellow adventurers, share your ideas, report bugs and help shape the future of Midgard.
            </p>
            <p className="mt-2 text-white/50 text-xs leading-relaxed">
              Verified bug reports may earn Runegold rewards.
            </p>
          </div>
          <a
            href={DISCORD_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl font-semibold text-white bg-[#5865F2] hover:bg-[#4752c4] transition-colors"
            data-testid="landing-discord-btn"
          >
            <DiscordGlyph className="w-5 h-5" /> Join Our Discord
          </a>
        </div>
      </section>

      <SiteFooter tone="overlay" className="relative z-10 w-full" />
    </div>
  );
}
